import { IValidatorMetadata } from '../../extensibility/metadatas/validator/validator-metadata';
import { IConstraint, IStringConstraint, INumberConstraint } from '../../model/base/annotation';
import { Metamodel } from 'app/model/base/model';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';
import { IDatasetTypeMetadata } from 'app/extensibility/metadatas/resource-type/dataset-type-metadata';
import { adfModelTypeMap, formatTypeCopySourceType, formatTypeCopySinkType } from '../consts/adf-model-type-map-const';
import { stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { Location } from 'app/model/dataset/location/location';
import { isControlNotUnderFeatureFlag } from 'app/extensibility/utils/dynamic-control.util';
import { ISelectOption } from 'app/interfaces/ISelectOption';

import { AuthenticationType } from 'app/interfaces/IAuthenticationType';
import { IPropertyBag } from 'app/extensibility/property-bag';
import { Named } from 'shell-models-lib';
import { calcExpressionString, isExpressionString } from 'app/extensibility/utils/expression.util';
import type { WizardContext } from 'app/shared/components/wizard/types';

type ModelConstraint = IConstraint | IStringConstraint | INumberConstraint;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function convertDynamicValidatorToConstraint(validators: IValidatorMetadata[]): ModelConstraint {
    if (validators == null || validators.length === 0) {
        return undefined;
    }
    const constraint: ModelConstraint = {};
    validators.forEach((v) => {
        switch (v.type) {
            case 'required':
                constraint.required = (v.dependentAttributes && v.dependentAttributes.length > 0) ? v.dependentAttributes : true;
                break;
            case 'pattern':
                (constraint as IStringConstraint).pattern = new RegExp(v.pattern);
                break;
            case 'number':
                // todo. add more constraint validator
                (constraint as INumberConstraint).minimum = v.min;
                (constraint as INumberConstraint).maximum = v.max;
                break;
            case 'integer':
                (constraint as INumberConstraint).whole = true;
                break;
            default:
                // todo, handle validators
                break;
        }
    });
    return constraint;
}

export function disableConnectorInSelection(isDeprecated: boolean | string, entity?: IPropertyBag): boolean {
    return isExpressionString(isDeprecated) ? entity && calcExpressionString(isDeprecated, entity) : isDeprecated;
}

export function getLinkedserviceStoreSettingsControls(lsConfig: ILinkedServiceTypeMetadata, datasetType?: string, isStoreWriteSettings: boolean = false): ControlMetadata[] {
    if (datasetType != null && datasetType !== '') {
        if (!isStoreWriteSettings) {
            // read settings
            if (lsConfig.datasetTypeToStoreReadSettingControls?.[datasetType]) {
                return lsConfig.datasetTypeToStoreReadSettingControls[datasetType];
            }
        } else {
            // write settings
            if (lsConfig.datasetTypeToStoreWriteSettingControls?.[datasetType]) {
                return lsConfig.datasetTypeToStoreWriteSettingControls[datasetType];
            }
        }
    }
    return !isStoreWriteSettings ? lsConfig.storeReadSettingControls || [] : lsConfig.storeWriteSettingControls || [];
}

export function getLinkedserviceLocationControls(lsConfig: ILinkedServiceTypeMetadata): ControlMetadata[] {
    if (lsConfig?.isNonFilePathBasedLocation) {
        return lsConfig.nonFilePathBasedLocationControls || [];
    }
    return [];
}

export function getDatasetControls(datasetConfig: IDatasetTypeMetadata, isV2: boolean): ControlMetadata[] {
    if (isV2) {
        if (datasetConfig?.v2Controls != null) {
            return datasetConfig.v2Controls.filter((c) => isControlNotUnderFeatureFlag(c)) || [];
        }
    }
    if (datasetConfig?.controls != null) {
        return datasetConfig.controls.filter((c) => isControlNotUnderFeatureFlag(c)) || [];
    }
    return [];
}

export function getLookupControls(datasetConfig: IDatasetTypeMetadata, propertyBag?: IPropertyBag): ControlMetadata[] {
    if (datasetConfig?.lookupSourceControls != null) {
        return datasetConfig.lookupSourceControls.filter((c) => isControlNotUnderFeatureFlag(c, propertyBag)) || [];
    }
    return [];
}

export function getCopySourceControls(
    datasetConfig: IDatasetTypeMetadata,
    isV2?: boolean,
    propertyBag?: IPropertyBag,
    wizardContext?: WizardContext
): ControlMetadata[] {
    if (wizardContext === 'CopyJob') {
        // Copy Job Choose data is opt-in; never fall back to all pipeline source controls.
        return datasetConfig?.copyJob?.sourceControls?.filter((c) =>
            c.isActivityLevel && isControlNotUnderFeatureFlag(c, propertyBag)) ?? [];
    }
    if (isV2) {
        if (datasetConfig?.v2CopySourceControls != null) {
            return datasetConfig.v2CopySourceControls.filter((c) => isControlNotUnderFeatureFlag(c, propertyBag)) || [];
        }
    }
    if (datasetConfig?.copySourceControls != null) {
        return datasetConfig.copySourceControls.filter((c) => isControlNotUnderFeatureFlag(c, propertyBag)) || [];
    }
    return [];
}

export function getCopySinkControls(datasetConfig: IDatasetTypeMetadata, propertyBag?: IPropertyBag): ControlMetadata[] {
    if (datasetConfig == null) { return []; }
    const copySinkControls = (typeof datasetConfig.copySinkControls === 'function') ? datasetConfig.copySinkControls() : datasetConfig.copySinkControls;
    if (copySinkControls != null) {
        return copySinkControls.filter((c) => isControlNotUnderFeatureFlag(c, propertyBag) || c.showDynamically) || [];
    }
    return [];
}

export function isSqlTypeDataset(datasetType: string): boolean {
    return datasetType === adfModelTypeMap.azureSql.datasetType ||
        datasetType === adfModelTypeMap.azureSqlDW.datasetType ||
        datasetType === adfModelTypeMap.sqlServer.datasetType ||
        datasetType === adfModelTypeMap.sqlPool.datasetType ||
        datasetType === adfModelTypeMap.azureSqlMI.datasetType;
}

export function isNullPath(location: Location): boolean {
    if (location == null) {
        return true;
    }
    return stringIsNullOrEmpty(location.getLocation().getFullPath()) && Metamodel.modelOf(location).attributes.every((attr) => attr.templateValue(location) == null);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function attributeValueIsNull(obj: any, attributeName: string, detectNullString?: boolean): boolean {
    if (obj == null) {
        return true;
    }
    const flag = detectNullString ? obj[attributeName] == null || stringIsNullOrEmpty(obj[attributeName]) : obj[attributeName] == null;
    return flag && (!Metamodel.modelOf(obj).hasAttribute(attributeName) || Metamodel.modelOf(obj).attribute(attributeName).templateValue(obj) == null);
}

export function getSupportedFormatCopySourceSinkType(datasetType: string[], isSink: boolean = false): Array<formatTypeCopySourceType | formatTypeCopySinkType> {
    if (datasetType != null) {
        const result: Array<formatTypeCopySourceType | formatTypeCopySinkType> = [];
        datasetType.forEach((datasetType) => {
            switch (datasetType) {
                case adfModelTypeMap.delimitedText.datasetType: {
                    result.push(!isSink ? adfModelTypeMap.delimitedText.copySourceType as formatTypeCopySourceType : adfModelTypeMap.delimitedText.copySinkType as formatTypeCopySinkType);
                    break;
                }
                case adfModelTypeMap.parquet.datasetType: {
                    result.push(!isSink ? adfModelTypeMap.parquet.copySourceType as formatTypeCopySourceType : adfModelTypeMap.parquet.copySinkType as formatTypeCopySinkType);
                    break;
                }
                case adfModelTypeMap.json.datasetType: {
                    result.push(!isSink ? adfModelTypeMap.json.copySourceType as formatTypeCopySourceType : adfModelTypeMap.json.copySinkType as formatTypeCopySinkType);
                    break;
                }
                case adfModelTypeMap.avro.datasetType: {
                    result.push(!isSink ? adfModelTypeMap.avro.copySourceType as formatTypeCopySourceType : adfModelTypeMap.avro.copySinkType as formatTypeCopySinkType);
                    break;
                }
                case adfModelTypeMap.orc.datasetType: {
                    result.push(!isSink ? adfModelTypeMap.orc.copySourceType as formatTypeCopySourceType : adfModelTypeMap.orc.copySinkType as formatTypeCopySinkType);
                    break;
                }
                case adfModelTypeMap.binary.datasetType: {
                    result.push(!isSink ? adfModelTypeMap.binary.copySourceType as formatTypeCopySourceType : adfModelTypeMap.binary.copySinkType as formatTypeCopySinkType);
                    break;
                }
                default: {
                    break;
                }
            }
        });
        return result;
    }
    return [];
}

export function generateExternalLinkHtml(text: string, link: string): string {
    return `<a style="text-decoration:none; display:inline-flex; align-items: center" href="${link}" target="_blank">
                <div>${text}</div>
                <div class="madrid-icon icon-redirect"></div>
            </a>`;
}

// TODO: remove this, use the one in shell-model-libs
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function arrayIsNullOrEmpty(array: any[]): boolean {
    return array == null || array.length === 0;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function mergeSourceSinkToDataset(dataset: any, sourceSink: any, withoutAdditionalColumns?: boolean): void {
    let storeSettings;
    if (sourceSink.storeSettings) {
        storeSettings = sourceSink.storeSettings;
        delete sourceSink.storeSettings;
    }
    let formatSettings;
    if (sourceSink.formatSettings) {
        formatSettings = sourceSink.formatSettings;
        delete sourceSink.formatSettings;
    }
    let additionalColumns;
    if (sourceSink.additionalColumns) {
        if (!withoutAdditionalColumns) {
            additionalColumns = sourceSink.additionalColumns;
        }
        delete sourceSink.additionalColumns;
    }
    dataset.storeSettings = storeSettings;
    dataset.formatSettings = formatSettings;
    dataset.additionalColumns = additionalColumns;
    dataset.properties.typeProperties = { ...dataset.properties.typeProperties, ...sourceSink, ...storeSettings, ...formatSettings, additionalColumns };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function mergeCredentialOption(options: Array<ISelectOption<any>>): Array<ISelectOption<any>> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let copy: Array<ISelectOption<any>>;
    if (options) {
        copy = [...options];
        copy.push({
            label: stringResources.ModelName_Credential,
            value: AuthenticationType.Credential
        });
    }
    return copy;
}

export function extractDetailActivityID(errorMessage: string): string | undefined {
    if (errorMessage) {
        const result = new RegExp(`.Activity ID:(.*)\.`).exec(errorMessage);
        return result != null && result.length > 1 ? result[1] : undefined;
    }
    return undefined;
}

export function resultIsTrue(functionOrConst: (() => boolean) | boolean | string | undefined, entity?: Named): boolean {
    return typeof functionOrConst === 'function'
        ? functionOrConst()
        : isExpressionString(functionOrConst)
            ? calcExpressionString(functionOrConst, entity)
            : Boolean(functionOrConst);
}
